import React, { useCallback, useEffect, useRef, useState } from 'react';
import Table from '../../components/table';
import columns from './columns';
import { useDispatch, useSelector } from 'react-redux';
import API from '../../services/api';
import { setAlert } from '../../redux/dashboard/actions';
import utils from '../../utils';
import { getSelectedCompany } from '../../redux/company/actions';

const CampaignTable = ({
  project,
  searchBy,
  isItemDetailsClosed,
  lastUpdated,
  onItemSelected,
  onCampaignCountChanged,
  onCheckboxSelected
}) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageCount, setPageCount] = useState(0);
  const [itemsCount, setItemsCount] = useState(0);
  const [initialSortColumn] = useState('displayName');
  const [selectedCampaigns] = useState(null);

  const nextPageToken = useRef('');
  const filters = useRef('');
  const sorting = useRef('displayName, id');
  const campaigns = useRef([]);
  const oldProjectId = useRef('');
  const oldLastUpdated = useRef(lastUpdated);

  const dispatch = useDispatch();
  const company = useSelector(getSelectedCompany);

  useEffect(() => {
    if (onCheckboxSelected && selectedCampaigns !== null) {
      onCheckboxSelected(selectedCampaigns);
    }
  }, [selectedCampaigns]);

  const getFilters = () => {
    if (searchBy !== '') {
      return `(displayName ILIKE "%${searchBy}%" OR id ILIKE "%${searchBy}%")`;
    }
    return '';
  };

  const getSorting = sortBy => {
    if (sortBy.length > 0 && sortBy[0].id) {
      return `${sortBy[0].id} ${sortBy[0].desc ? 'DESC' : 'ASC'}, id`;
    }

    return (sorting.current = 'createTime DESC, id');
  };

  const getCampaignList = (newCampaigns, forceUpdate) => {
    // Merge new data with previous data if sorting and filters hasn't change
    if (!forceUpdate) {
      return [...campaigns.current, ...newCampaigns];
    }

    // Overwrite list with new data
    return newCampaigns;
  };

  const fetchData = useCallback(
    ({ pageIndex, pageSize, sortBy }) => {
      setLoading(true);
      // Keep old sorting and filters to handle changes
      const oldSorting = sorting.current;
      const oldFilters = filters.current;

      // Apply Sorting and filters
      filters.current = encodeURI(getFilters());
      sorting.current = getSorting(sortBy);

      const forceUpdate =
        oldSorting !== sorting.current ||
        oldFilters !== filters.current ||
        lastUpdated !== oldLastUpdated.current ||
        project?.id !== oldProjectId.current;

      // Clear pageToken if project, filter or sorting has changed
      if (forceUpdate) {
        nextPageToken.current = '';
        setLoading(true);
        oldLastUpdated.current = lastUpdated;
      }

      // Fetch new page
      if (!company) {
        setData([]);
        setLoading(false);
        return;
      }

      API.getCampaigns(company.id, nextPageToken.current, filters.current, 15, sorting.current)
        .then(response => {
          campaigns.current = getCampaignList(response.campaigns, forceUpdate);

          nextPageToken.current = response.nextPageToken;
          oldProjectId.current = project.id;

          setItemsCount(response.totalSize);
          onCampaignCountChanged(response.totalSize);

          // Update table indexes
          const startRow = pageSize * pageIndex;
          const endRow = startRow + pageSize;

          // Slice items from campaign list
          const slicedCampaignsData = campaigns.current.slice(startRow, endRow);
          setData(slicedCampaignsData);

          // Compute total page count
          setPageCount(Math.ceil(response.totalSize / pageSize));
          setLoading(false);
        })
        .catch(() => {
          dispatch(
            setAlert(
              utils.generateAlert(
                'You have no permission to see campaigns on this Site group. Try choosing another one',
                'error'
              )
            )
          );
          setData([]);
          setLoading(false);
        });
    },
    [searchBy, initialSortColumn, project, lastUpdated]
  );

  return (
    <Table
      columns={columns}
      data={data}
      dataLabel={'campaigns'}
      fetchData={fetchData}
      loading={loading}
      searchBy={searchBy}
      pageCount={pageCount}
      itemsCount={itemsCount}
      resetOnChange={project} // reset table when project change
      hasInteractiveRows
      initialSortColumn={initialSortColumn.id}
      isItemDetailsClosed={isItemDetailsClosed}
      onItemSelected={onItemSelected}
      // bulkActionButtons
      // onDataSelected={setSelectedCampaigns}
    />
  );
};

export default CampaignTable;
